import { useEffect, useRef } from "react"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import {
  COMMAND_PRIORITY_HIGH,
  DRAGOVER_COMMAND,
  DRAGSTART_COMMAND,
  DROP_COMMAND,
  $getRoot,
  $getNodeByKey,
  $getNearestNodeFromDOMNode,
  $isElementNode,
  $isRootNode,
  $isTextNode,
  type ElementNode,
  type LexicalNode,
} from "lexical"
import { MERGE_TAG_DRAG_TYPE } from "./constants"
import { $createMergeTagNode, $isMergeTagNode } from "./MergeTagNode"
import {
  clearActiveMergeTagDrag,
  getActiveMergeTagDrag,
  parseMergeTagDragPayload,
} from "./mergeTagDragStore"

const MERGE_TAG_PILL_SELECTOR = "[data-merge-tag-pill]"

const isMergeTagDrag = (dataTransfer: DataTransfer | null): boolean => (
  (dataTransfer?.types.includes(MERGE_TAG_DRAG_TYPE) ?? false)
  || getActiveMergeTagDrag() !== null
)

type CaretPositionLike = {
  offsetNode: Node
  offset: number
}

type DocumentWithCaretPosition = Document & {
  caretPositionFromPoint?: (x: number, y: number) => CaretPositionLike | null
}

const getCaretRangeFromPoint = (x: number, y: number): Range | null => {
  if (document.caretRangeFromPoint) {
    return document.caretRangeFromPoint(x, y)
  }

  const doc = document as DocumentWithCaretPosition
  const caretPositionFromPoint = doc.caretPositionFromPoint?.bind(doc)
  if (!caretPositionFromPoint) return null

  const position = caretPositionFromPoint(x, y)
  if (!position) return null

  const range = document.createRange()
  range.setStart(position.offsetNode, position.offset)
  range.collapse(true)

  return range
}

const getPillFromRange = (range: Range, rootElement: HTMLElement): HTMLElement | null => {
  let node: Node | null = range.startContainer
  if (node.nodeType === Node.TEXT_NODE) {
    node = node.parentElement
  }

  const pill = (node as Element | null)?.closest?.(MERGE_TAG_PILL_SELECTOR) as HTMLElement | null

  return pill && rootElement.contains(pill) ? pill : null
}

const findPillAtPoint = (
  x: number,
  y: number,
  rootElement: HTMLElement,
): HTMLElement | null => {
  const elements = document.elementsFromPoint?.(x, y) ?? []
  const fromStack = elements.find(
    (el) => el instanceof HTMLElement
      && rootElement.contains(el)
      && el.closest(MERGE_TAG_PILL_SELECTOR),
  ) as HTMLElement | undefined

  if (fromStack) {
    return fromStack.closest(MERGE_TAG_PILL_SELECTOR) as HTMLElement
  }

  const element = document.elementFromPoint(x, y)
  if (!element || !rootElement.contains(element)) return null

  return element.closest(MERGE_TAG_PILL_SELECTOR) as HTMLElement | null
}

type IndicatorPosition = {
  left: number
  top: number
  height: number
}

const toIndicatorCoords = (
  caretRect: DOMRect,
  positionParent: HTMLElement,
  lineHeight: number,
): IndicatorPosition => {
  const parentRect = positionParent.getBoundingClientRect()
  const height = caretRect.height > 0 ? caretRect.height : lineHeight

  return {
    left: caretRect.left - parentRect.left,
    top: caretRect.top - parentRect.top,
    height,
  }
}

const getIndicatorPosition = (
  clientX: number,
  clientY: number,
  rootElement: HTMLElement,
  positionParent: HTMLElement,
): IndicatorPosition | null => {
  const rootRect = rootElement.getBoundingClientRect()
  const lineHeight = Math.min(rootRect.height, 28)

  const range = getCaretRangeFromPoint(clientX, clientY)
  if (range) {
    const pillFromRange = getPillFromRange(range, rootElement)
    if (pillFromRange) {
      const pillRect = pillFromRange.getBoundingClientRect()
      const insertBefore = clientX < pillRect.left + pillRect.width / 2
      const caretRect = new DOMRect(
        insertBefore ? pillRect.left : pillRect.right,
        pillRect.top,
        0,
        pillRect.height || lineHeight,
      )

      return toIndicatorCoords(caretRect, positionParent, lineHeight)
    }

    const container = range.startContainer
    const domNode = container.nodeType === Node.TEXT_NODE
      ? container.parentElement
      : container as HTMLElement

    if (domNode && rootElement.contains(domNode)) {
      let caretRect = range.getBoundingClientRect()
      if (caretRect.width === 0 && caretRect.height === 0) {
        caretRect = new DOMRect(
          clientX,
          rootRect.top + (rootRect.height - lineHeight) / 2,
          0,
          lineHeight,
        )
      }

      return toIndicatorCoords(caretRect, positionParent, lineHeight)
    }
  }

  const pill = findPillAtPoint(clientX, clientY, rootElement)
  if (pill) {
    const pillRect = pill.getBoundingClientRect()
    const insertBefore = clientX < pillRect.left + pillRect.width / 2
    const caretRect = new DOMRect(
      insertBefore ? pillRect.left : pillRect.right,
      pillRect.top,
      0,
      pillRect.height || lineHeight,
    )

    return toIndicatorCoords(caretRect, positionParent, lineHeight)
  }

  const isOverEditor = (
    clientX >= rootRect.left
    && clientX <= rootRect.right
    && clientY >= rootRect.top
    && clientY <= rootRect.bottom
  )
  if (isOverEditor) {
    const caretRect = new DOMRect(
      clientX,
      rootRect.top + (rootRect.height - lineHeight) / 2,
      0,
      lineHeight,
    )

    return toIndicatorCoords(caretRect, positionParent, lineHeight)
  }

  return null
}

// Resolve screen coordinates to a Lexical text position
const getDropPosition = (
  event: DragEvent,
  editor: ReturnType<typeof useLexicalComposerContext>[0],
) => {
  const rootElement = editor.getRootElement()
  if (!rootElement) return null

  const range = getCaretRangeFromPoint(event.clientX, event.clientY)
  if (range) {
    const pillFromRange = getPillFromRange(range, rootElement)
    if (pillFromRange) {
      const pillRect = pillFromRange.getBoundingClientRect()
      const insertBefore = event.clientX < pillRect.left + pillRect.width / 2
      const x = insertBefore ? pillRect.left : pillRect.right
      const edgeRange = getCaretRangeFromPoint(x, event.clientY)

      if (edgeRange) {
        return {
          startContainer: edgeRange.startContainer,
          startOffset: edgeRange.startOffset,
          insertBeforePill: pillFromRange,
        }
      }
    }

    const { startContainer, startOffset } = range
    const domNode = startContainer.nodeType === Node.TEXT_NODE
      ? startContainer.parentElement
      : startContainer

    if (domNode && rootElement.contains(domNode as Node)) {
      return { startContainer, startOffset, insertBeforePill: null as HTMLElement | null }
    }
  }

  const pill = findPillAtPoint(event.clientX, event.clientY, rootElement)
  if (pill) {
    const pillRect = pill.getBoundingClientRect()
    const insertBefore = event.clientX < pillRect.left + pillRect.width / 2
    const x = insertBefore ? pillRect.left : pillRect.right
    const edgeRange = getCaretRangeFromPoint(x, event.clientY)

    if (edgeRange) {
      return {
        startContainer: edgeRange.startContainer,
        startOffset: edgeRange.startOffset,
        insertBeforePill: pill,
      }
    }
  }

  const rootRect = rootElement.getBoundingClientRect()
  const isOverEditor = (
    event.clientX >= rootRect.left
    && event.clientX <= rootRect.right
    && event.clientY >= rootRect.top
    && event.clientY <= rootRect.bottom
  )
  if (isOverEditor) {
    const midX = rootRect.left + rootRect.width / 2
    const midY = rootRect.top + rootRect.height / 2
    const fallbackRange = getCaretRangeFromPoint(event.clientX, event.clientY)
      ?? getCaretRangeFromPoint(midX, midY)

    if (fallbackRange) {
      return {
        startContainer: fallbackRange.startContainer,
        startOffset: fallbackRange.startOffset,
        insertBeforePill: null,
      }
    }
  }

  return null
}

const insertAtElementChildOffset = (
  parent: ElementNode,
  offset: number,
  nodeToInsert: LexicalNode,
): boolean => {
  const childAtOffset = parent.getChildAtIndex(offset)
  if (childAtOffset && childAtOffset !== nodeToInsert) {
    childAtOffset.insertBefore(nodeToInsert)

    return true
  }

  const childBefore = parent.getChildAtIndex(offset - 1)
  if (childBefore && childBefore !== nodeToInsert) {
    childBefore.insertAfter(nodeToInsert)

    return true
  }

  if (parent.getChildrenSize() === 0) {
    parent.append(nodeToInsert)

    return true
  }

  return false
}

const insertAtStartOfEditor = (nodeToInsert: LexicalNode): boolean => {
  const root = $getRoot()
  const firstBlock = root.getFirstChild()
  if (!firstBlock || !$isElementNode(firstBlock) || firstBlock === nodeToInsert) {
    return false
  }

  const firstChild = firstBlock.getFirstChild()
  if (firstChild && firstChild !== nodeToInsert) {
    firstChild.insertBefore(nodeToInsert)

    return true
  }

  if (firstBlock.getChildrenSize() === 0) {
    firstBlock.append(nodeToInsert)

    return true
  }

  return false
}

const performMergeTagDrop = (
  event: DragEvent,
  editor: ReturnType<typeof useLexicalComposerContext>[0],
  dropHandledRef: { current: boolean },
): boolean => {
  if (dropHandledRef.current) {
    return true
  }

  const payload = parseMergeTagDragPayload(event.dataTransfer)
  if (!payload) return false

  const { nodeKey, tagId, tagLabel } = payload
  const dropPos = getDropPosition(event, editor)
  if (!dropPos) return false

  editor.update(() => {
    const draggedNode = nodeKey ? $getNodeByKey(nodeKey) : null
    const nodeToInsert = $isMergeTagNode(draggedNode)
      ? draggedNode
      : $createMergeTagNode(tagId, tagLabel)

    const rootElement = editor.getRootElement()
    const rootRect = rootElement?.getBoundingClientRect()
    // Allow dropping into the far-left padding area to mean "insert at position 0".
    const isAtStartEdge = rootRect ? event.clientX <= rootRect.left + 16 : false
    if (isAtStartEdge && insertAtStartOfEditor(nodeToInsert)) {
      nodeToInsert.selectNext(0, 0)

      return
    }

    if (dropPos.insertBeforePill) {
      const targetLexicalNode = $getNearestNodeFromDOMNode(dropPos.insertBeforePill)

      if (targetLexicalNode === nodeToInsert) {
        return
      }

      if ($isMergeTagNode(targetLexicalNode)) {
        const pillRect = dropPos.insertBeforePill.getBoundingClientRect()
        const insertBefore = event.clientX < pillRect.left + pillRect.width / 2
        if (insertBefore) {
          targetLexicalNode.insertBefore(nodeToInsert)
        } else {
          targetLexicalNode.insertAfter(nodeToInsert)
        }
      } else {
        targetLexicalNode?.insertAfter(nodeToInsert)
      }
    } else {
      const { startContainer, startOffset } = dropPos

      if (startContainer.nodeType === Node.ELEMENT_NODE) {
        const lexicalParent = $getNearestNodeFromDOMNode(startContainer)
          ?? $getRoot()

        const insertionParent = $isRootNode(lexicalParent)
          ? lexicalParent.getFirstChild()
          : lexicalParent

        if (
          insertionParent
          && $isElementNode(insertionParent)
          && insertAtElementChildOffset(insertionParent, startOffset, nodeToInsert)
        ) {
          nodeToInsert.selectNext(0, 0)

          return
        }
      }

      const resolvedTarget = $getNearestNodeFromDOMNode(startContainer)
      if (!resolvedTarget) return

      if (resolvedTarget === nodeToInsert) {
        return
      }

      if ($isTextNode(resolvedTarget)) {
        const [, rightPart] = resolvedTarget.splitText(startOffset)
        if (rightPart) {
          rightPart.insertBefore(nodeToInsert)
        } else {
          resolvedTarget.insertAfter(nodeToInsert)
        }
      } else if ($isMergeTagNode(resolvedTarget)) {
        const tagDom = editor.getElementByKey(resolvedTarget.getKey())
        const insertBefore = tagDom
          ? (
            event.clientX
              < tagDom.getBoundingClientRect().left + tagDom.getBoundingClientRect().width / 2
          )
          : resolvedTarget.getPreviousSibling() === null

        if (insertBefore) {
          resolvedTarget.insertBefore(nodeToInsert)
        } else {
          resolvedTarget.insertAfter(nodeToInsert)
        }
      } else if ($isElementNode(resolvedTarget)) {
        insertAtElementChildOffset(resolvedTarget, startOffset, nodeToInsert)
      } else {
        resolvedTarget.insertAfter(nodeToInsert)
      }
    }

    nodeToInsert.selectNext(0, 0)
  })

  // Ref flag consumed by DROP_COMMAND and native drop handlers.
  // eslint-disable-next-line no-param-reassign
  dropHandledRef.current = true
  editor.focus()
  clearActiveMergeTagDrag()

  return true
}

const DragDropPlugin = () => {
  const [editor] = useLexicalComposerContext()
  const dropIndicatorRef = useRef<HTMLDivElement | null>(null)
  const lastDragPointRef = useRef<{ x: number; y: number } | null>(null)
  const dropHandledRef = useRef(false)

  useEffect(() => {
    const indicator = document.createElement("div")
    indicator.setAttribute("aria-hidden", "true")
    indicator.style.cssText = [
      "position: absolute",
      "width: 2px",
      "background: #3182CE",
      "pointer-events: none",
      "z-index: 20",
      "display: none",
      "border-radius: 1px",
    ].join(";")
    dropIndicatorRef.current = indicator

    const rootElement = editor.getRootElement()
    if (rootElement) {
      const positionParent = rootElement.parentElement ?? rootElement
      if (getComputedStyle(positionParent).position === "static") {
        positionParent.style.position = "relative"
      }
      positionParent.appendChild(indicator)
    }

    return () => {
      indicator.remove()
    }
  }, [editor])

  useEffect(() => {
    const hideIndicator = () => {
      if (dropIndicatorRef.current) {
        dropIndicatorRef.current.style.display = "none"
      }
    }

    const showIndicatorAtPoint = (clientX: number, clientY: number) => {
      const indicator = dropIndicatorRef.current
      const rootElement = editor.getRootElement()
      const positionParent = rootElement?.parentElement ?? rootElement
      if (!indicator || !rootElement || !positionParent) return

      const position = getIndicatorPosition(
        clientX,
        clientY,
        rootElement,
        positionParent,
      )
      if (!position) {
        hideIndicator()

        return
      }

      indicator.style.display = "block"
      indicator.style.left = `${position.left}px`
      indicator.style.top = `${position.top}px`
      indicator.style.height = `${position.height}px`
    }

    const showIndicator = (event: DragEvent) => {
      if (!isMergeTagDrag(event.dataTransfer)) {
        hideIndicator()

        return
      }

      lastDragPointRef.current = { x: event.clientX, y: event.clientY }
      showIndicatorAtPoint(event.clientX, event.clientY)
    }

    const handleNativeDragOver = (event: DragEvent) => {
      const { dataTransfer } = event
      if (!isMergeTagDrag(dataTransfer)) return

      event.preventDefault()
      if (dataTransfer) {
        dataTransfer.dropEffect = getActiveMergeTagDrag()?.nodeKey ? "move" : "copy"
      }
      showIndicator(event)
    }

    const handleNativeDrop = (event: DragEvent) => {
      if (!isMergeTagDrag(event.dataTransfer)) return

      if (performMergeTagDrop(event, editor, dropHandledRef)) {
        event.preventDefault()
        event.stopPropagation()
        hideIndicator()
      }
    }

    const unregisterDragstart = editor.registerCommand(
      DRAGSTART_COMMAND,
      (event: DragEvent) => {
        if (isMergeTagDrag(event.dataTransfer)) {
          return true
        }

        return false
      },
      COMMAND_PRIORITY_HIGH,
    )

    const unregisterDragover = editor.registerCommand(
      DRAGOVER_COMMAND,
      (event: DragEvent) => {
        const { dataTransfer } = event
        if (!isMergeTagDrag(dataTransfer)) {
          return false
        }
        event.preventDefault()
        if (dataTransfer) {
          dataTransfer.dropEffect = getActiveMergeTagDrag()?.nodeKey ? "move" : "copy"
        }
        showIndicator(event)

        return true
      },
      COMMAND_PRIORITY_HIGH,
    )

    const unregisterDrop = editor.registerCommand(
      DROP_COMMAND,
      (event: DragEvent) => {
        if (dropHandledRef.current) {
          return true
        }

        if (!performMergeTagDrop(event, editor, dropHandledRef)) {
          return false
        }

        event.preventDefault()
        hideIndicator()

        return true
      },
      COMMAND_PRIORITY_HIGH,
    )

    const rootElement = editor.getRootElement()
    const positionParent = rootElement?.parentElement ?? rootElement

    const handleDragLeave = (e: DragEvent) => {
      const related = e.relatedTarget as Node | null
      if (
        positionParent
        && related
        && !positionParent.contains(related)
      ) {
        hideIndicator()
      }
    }

    const handleDragEnd = () => {
      lastDragPointRef.current = null
      dropHandledRef.current = false
      clearActiveMergeTagDrag()
      hideIndicator()
    }

    const handleScroll = () => {
      const point = lastDragPointRef.current
      if (point) {
        showIndicatorAtPoint(point.x, point.y)
      }
    }

    rootElement?.addEventListener("dragover", handleNativeDragOver)
    rootElement?.addEventListener("drop", handleNativeDrop)
    rootElement?.addEventListener("dragleave", handleDragLeave)
    rootElement?.addEventListener("scroll", handleScroll)
    positionParent?.addEventListener("dragleave", handleDragLeave)
    document.addEventListener("dragend", handleDragEnd)

    return () => {
      unregisterDragstart()
      unregisterDragover()
      unregisterDrop()
      rootElement?.removeEventListener("dragover", handleNativeDragOver)
      rootElement?.removeEventListener("drop", handleNativeDrop)
      rootElement?.removeEventListener("dragleave", handleDragLeave)
      rootElement?.removeEventListener("scroll", handleScroll)
      positionParent?.removeEventListener("dragleave", handleDragLeave)
      document.removeEventListener("dragend", handleDragEnd)
    }
  }, [editor])

  return null
}

export default DragDropPlugin
